import { useRef, type JSX, type KeyboardEvent, type ReactNode, type Ref } from 'react';

/**
 * OS-1725 C3 — pestañas con el patrón WAI-ARIA (tablist / tab / tabpanel):
 * - solo la pestaña activa es parada de tabulación (tabindex móvil);
 * - ←/→ recorren las pestañas (con vuelta), Inicio/Fin van a la primera/última, y la pestaña
 *   enfocada se activa al llegar (activación automática);
 * - la pestaña activa apunta al panel (`aria-controls`) y el panel se rotula con ella.
 * Sin estilos propios: recibe las clases de quien lo usa (no cambia el diseño).
 */
export interface TabItem<T extends string> {
  id: T;
  label: ReactNode;
  icon?: ReactNode;
}

interface TabsProps<T extends string> {
  items: ReadonlyArray<TabItem<T>>;
  value: T;
  onChange: (id: T) => void;
  /** Nombre accesible de la lista de pestañas. */
  label: string;
  idPrefix: string;
  className?: string;
  tabClassName?: string;
  activeTabClassName?: string;
  listRef?: Ref<HTMLDivElement>;
}

export function tabElementId(idPrefix: string, id: string): string {
  return `${idPrefix}-tab-${id}`;
}

export function tabPanelElementId(idPrefix: string): string {
  return `${idPrefix}-panel`;
}

export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
  idPrefix,
  className,
  tabClassName,
  activeTabClassName,
  listRef,
}: TabsProps<T>): JSX.Element {
  const buttons = useRef(new Map<T, HTMLButtonElement>());

  function activate(index: number): void {
    const next = items[(index + items.length) % items.length];
    if (!next) {return;}
    onChange(next.id);
    buttons.current.get(next.id)?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    const moves: Record<string, number> = {
      ArrowRight: index + 1,
      ArrowLeft: index - 1,
      Home: 0,
      End: items.length - 1,
    };
    const target = moves[event.key];
    if (target === undefined) {return;}
    event.preventDefault();
    activate(target);
  }

  return (
    <div className={className} role="tablist" aria-label={label} ref={listRef}>
      {items.map((item, index) => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            ref={(element) => {
              if (element) {buttons.current.set(item.id, element);}
              else {buttons.current.delete(item.id);}
            }}
            type="button"
            role="tab"
            id={tabElementId(idPrefix, item.id)}
            aria-selected={selected}
            aria-controls={selected ? tabPanelElementId(idPrefix) : undefined}
            tabIndex={selected ? 0 : -1}
            className={selected ? activeTabClassName : tabClassName}
            onClick={() => onChange(item.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {item.icon}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

interface TabPanelProps {
  idPrefix: string;
  value: string;
  className?: string;
  children: ReactNode;
}

/** Panel de la pestaña activa: enfocable para que el contenido se alcance con Tab desde la lista. */
export function TabPanel({ idPrefix, value, className, children }: TabPanelProps): JSX.Element {
  return (
    <div
      role="tabpanel"
      id={tabPanelElementId(idPrefix)}
      aria-labelledby={tabElementId(idPrefix, value)}
      tabIndex={0}
      className={className}
    >
      {children}
    </div>
  );
}
